import React, { useEffect, useState } from 'react';
import { Dialog, Input } from '@fx-ui/fine-design';
import { ECode, SITE_ROUTER } from '~/constants';
import { Hyperlink } from '@fx-ui/jdy-design-biz';
import { IconFont, LoginPassword } from '~/components';
import { get, getQueryRouter, hasFunctionGoogle } from '~/utils';
import Intl from '@fx-ui/intl';

interface IPasswordModify {
    visible: boolean;
    userInfo: Record<string, any>;
    hasOldPass: boolean;
    onClose?: () => void;
    onModifySuccess?: (oldPassValue: string, newPassValue: string) => Promise<void>;
}

const PasswordModifyDialog: React.FunctionComponent<IPasswordModify> = (props: IPasswordModify) => {
    const { visible, userInfo, hasOldPass, onClose, onModifySuccess } = props;

    const [oldPassValue, setOldPassValue] = useState<string>('');
    const [newPassValue, setNewPassValue] = useState<string>('');
    const [isNewPassValid, setIsNewPassValid] = useState<boolean>(false);

    const [confirmPassState, setConfirmPassState] = useState<'error' | 'normal'>('normal');
    const [confirmPassValue, setConfirmPassValue] = useState<string>('');
    const [errorTip, setErrorTip] = useState<string>('');
    const [showPasswordPrev, setShowPasswordPrev] = useState(false);
    const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);

    useEffect(() => {
        if (!visible) {
            setOldPassValue('');
            setNewPassValue('');
            setConfirmPassValue('');
            setErrorTip('');
        }
    }, [visible]);

    const renderContent = () => {
        return (
            <div className="modify-content">
                {errorTip && <pre className="error-tip">{errorTip}</pre>}
                {hasOldPass && (
                    <>
                        <div className="label">{Intl.get('password.original')}</div>
                        <Input
                            className="modify-input"
                            value={oldPassValue}
                            type={showPasswordPrev ? 'text' : 'password'}
                            onChange={(val) => {
                                setOldPassValue(val);
                            }}
                            suffix={
                                <IconFont
                                    className="eye-suffix"
                                    type={showPasswordPrev ? 'eye-close' : 'eye-open'}
                                    onClick={() =>
                                        setShowPasswordPrev((v) => {
                                            return !v;
                                        })
                                    }
                                />
                            }
                        />
                        <div className="forget-password">
                            <Hyperlink
                                compact
                                underline={false}
                                target="_self"
                                className="forget-btn"
                                href={getQueryRouter(
                                    hasFunctionGoogle()
                                        ? [
                                              {
                                                  key: 'email',
                                                  value: get(userInfo, 'email', '')
                                              }
                                          ]
                                        : [],
                                    SITE_ROUTER.AUTHENTICATION.FORGOT_PASSWORD
                                )} // 如果是海外版则添加email参数
                                tabIndex={-1}
                            >
                                {Intl.get('password.forget')}
                            </Hyperlink>
                        </div>
                    </>
                )}
                <div className="label">{Intl.get('password.new')}</div>
                <LoginPassword
                    className="modify-input password"
                    waterMark={Intl.get('password.new')}
                    value={newPassValue}
                    onBlur={(val, valid) => {
                        setNewPassValue(val);
                        setIsNewPassValid(valid);
                    }}
                    userInfo={userInfo}
                />
                <div className="label">{Intl.get('password.confirm')}</div>
                <Input
                    className="modify-input"
                    placeholder={Intl.get('password.confirm')}
                    inputState={confirmPassState}
                    value={confirmPassValue}
                    type={showPasswordConfirm ? 'text' : 'password'}
                    onChange={(val) => {
                        setConfirmPassValue(val);
                    }}
                    suffix={
                        <IconFont
                            className="eye-suffix"
                            type={showPasswordConfirm ? 'eye-close' : 'eye-open'}
                            onClick={() =>
                                setShowPasswordConfirm((v) => {
                                    return !v;
                                })
                            }
                        />
                    }
                />
            </div>
        );
    };

    const dealModify = () => {
        // 新密码格式错误
        if (!isNewPassValid) {
            setErrorTip(Intl.get('account.password.mismatch.rule'));
            return;
        }
        // 两次密码不一致
        if (confirmPassValue !== newPassValue) {
            setConfirmPassState('error');
            setErrorTip(Intl.get('invalid.confirmPass'));
            return;
        } else {
            setConfirmPassState('normal');
        }
        setErrorTip('');
        if (onModifySuccess) {
            onModifySuccess(oldPassValue, newPassValue).catch((err) => {
                const errCode = get(err, 'response.data.code');
                if (errCode === ECode.USER_PASSWORD_HISTORY_DUPLICATE) {
                    setErrorTip(Intl.get('account.passReset.fail.history'));
                }
            });
        }
    };

    const dealClose = () => {
        setConfirmPassState('normal');
        onClose && onClose();
    };

    return (
        <Dialog
            className="fx-password-modify-dialog"
            title={hasOldPass ? Intl.get('password.update') : Intl.get('password.set')}
            visible={visible}
            width={400}
            height={hasOldPass ? 470 : 382}
            text4Ok={Intl.get('save')}
            separator={{ footer: false }}
            onCancel={dealClose}
            onOk={dealModify}
        >
            {renderContent()}
        </Dialog>
    );
};

export default PasswordModifyDialog;
